import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import {
	ResultsCategoriesMenu,
	ResultsData,
	ResultsSportsMenu,
} from "@features/results/desktop/components";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { ResultsMenuStoreContext } from "@state/context/results";
import { ResultsMenuStore } from "@state/stores/results";
import { setTabTitle } from "@utils";
import { useNotification } from "@utils/notifications";
import classnames from "classnames";

export default observer(function ResultsPage() {
	const rootStore = useRootAppStore();
	const resultsMenuStore = useMemo(() => new ResultsMenuStore(rootStore), []);
	const { isMenuExpanded, onDispose } = resultsMenuStore;
	const { hasPendingRequest, error } = resultsMenuStore.resultsDataStore;

	const { t } = useTranslation();
	const { showError } = useNotification();

	useScrollRestoration({
		type: ConfigType.Basic,
		isLoading: hasPendingRequest,
	});

	useEffect(() => {
		setTabTitle(t("RESULTS.TITLE"));

		return () => onDispose();
	}, []);

	useEffect(() => {
		if (error?.message) {
			showError(error.message);
		}
	}, [error]);

	const asideClasses = classnames("l--aside--left", {
		"is-expanded": isMenuExpanded,
	});

	const mainClasses = classnames("l--main l--main--med", {
		"sidebar-left-expanded": isMenuExpanded,
		"sidebar-left-collapsed": !isMenuExpanded,
	});

	return (
		<DesktopLayout
			headerType={HeaderType.WITH_SIDEBAR}
			mainClassName={mainClasses}
		>
			<ResultsMenuStoreContext.Provider value={resultsMenuStore}>
				<aside className={asideClasses}>
					<ResultsSportsMenu />
					<ResultsCategoriesMenu />
				</aside>
				<ResultsData />
			</ResultsMenuStoreContext.Provider>
		</DesktopLayout>
	);
});
